/**
 * useSpectatorProjectData — Fetches project data via the public spectator endpoint.
 *
 * Cloned from useProjectData but uses /api/v1/workspace/spectator/{shareId}/project/{projectId}
 * instead of authenticated project API calls.
 */
import { useState, useCallback, useRef } from 'react'
import axios from 'axios'
import type { ProjectDetail, ActivityWithExchanges } from '@/lib/types/project-graph'

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.PROD ? '/api/v1' : 'http://localhost:8880/api/v1')

interface UseSpectatorProjectDataReturn {
  project: ProjectDetail | null
  activities: ActivityWithExchanges[]
  loading: boolean
  error: string | null
  currentProjectId: string | null
  loadProject: (projectId: string) => Promise<void>
  refreshProject: () => Promise<void>
  clearProject: () => void
}

export function useSpectatorProjectData(
  shareId: string,
  authToken?: string | null,
): UseSpectatorProjectDataReturn {
  const [project, setProject] = useState<ProjectDetail | null>(null)
  const [activities, setActivities] = useState<ActivityWithExchanges[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currentProjectIdRef = useRef<string | null>(null)
  const fetchIdRef = useRef(0)

  const fetchProjectData = useCallback(
    async (projectId: string, clearFirst: boolean) => {
      const myFetchId = ++fetchIdRef.current

      if (clearFirst) {
        setActivities([])
        setProject(null)
      }
      setLoading(true)
      setError(null)
      currentProjectIdRef.current = projectId

      try {
        // Use the isLocalDev check to determine the correct API base
        const isLocalDev =
          window.location.hostname === 'localhost' || window.location.hostname.startsWith('192.168')
        const base = isLocalDev ? 'https://ecosemantic.com/api/v1' : API_BASE

        const headers: Record<string, string> = {}
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`
        }

        const resp = await axios.get(
          `${base}/workspace/spectator/${shareId}/project/${projectId}`,
          { params: { _t: Date.now() }, headers }, // cache-bust: Cloudflare caches GET with max-age=30
        )

        if (fetchIdRef.current !== myFetchId) return

        const data = resp.data
        setProject(data.project || null)
        setActivities(data.activities || [])
      } catch (err) {
        if (fetchIdRef.current !== myFetchId) return
        const message = err instanceof Error ? err.message : 'Failed to load project'
        setError(message)
      } finally {
        if (fetchIdRef.current === myFetchId) {
          setLoading(false)
        }
      }
    },
    [shareId, authToken],
  )

  const loadProject = useCallback(
    async (projectId: string) => {
      await fetchProjectData(projectId, true)
    },
    [fetchProjectData],
  )

  const refreshProject = useCallback(async () => {
    if (currentProjectIdRef.current) {
      await fetchProjectData(currentProjectIdRef.current, false)
    }
  }, [fetchProjectData])

  const clearProject = useCallback(() => {
    setProject(null)
    setActivities([])
    setError(null)
    currentProjectIdRef.current = null
  }, [])

  return {
    project,
    activities,
    loading,
    error,
    currentProjectId: currentProjectIdRef.current,
    loadProject,
    refreshProject,
    clearProject,
  }
}
